import { useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { InfoIcon } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Lifts the panel one step and lights its top edge. Reserve it for the one
   * panel that leads a page — if two panels on a screen are emphasised,
   * neither is.
   */
  featured?: boolean;
  /** Raises on hover. Only for panels that are themselves a target. */
  interactive?: boolean;
}

/**
 * The surface every panel on a page sits on.
 *
 * One component rather than a repeated `rounded-xl border bg-...` string, so
 * that changing what "a panel" looks like is one edit instead of a grep. The
 * fill, border and shadow come from `.panel` in the stylesheet, which is where
 * the two themes are reconciled.
 *
 * Elevation here is the white fill against the grey canvas, not the shadow —
 * which is why the shadow can stay as soft as it is. `featured` steps it up one
 * notch for the single panel that leads a page.
 */
export function Card({ featured, interactive, className, children, ...props }: CardProps) {
  return (
    <div
      className={cn(
        "panel rounded-panel relative overflow-hidden",
        featured && "shadow-md",
        interactive &&
          cn(
            "transition-all duration-200 ease-out-expo",
            "hover:border-border-strong hover:-translate-y-0.5 hover:shadow-md",
          ),
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps {
  title: React.ReactNode;
  /** Small uppercase label above the title. */
  eyebrow?: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

/**
 * A panel's title row.
 *
 * The title and its actions only. The optional description is folded behind an
 * info glyph beside the title rather than printed as a paragraph: most of them
 * explain a panel once, and a line of small grey copy under every heading on
 * every screen is what made pages read as walls of text. It is still one hover
 * or one Tab away, and it stays attached to the heading for screen readers.
 */
export function CardHeader({ title, eyebrow, description, actions, className }: CardHeaderProps) {
  const hintId = useId();

  return (
    <div
      className={cn("flex min-h-14 items-center justify-between gap-4 px-5 pt-4 pb-1", className)}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <h2
          className="text-content truncate text-[0.9375rem] font-semibold tracking-tight"
          aria-describedby={description ? hintId : undefined}
        >
          {eyebrow ? <span className="sr-only">{eyebrow}: </span> : null}
          {title}
        </h2>
        {description ? <HeaderHint id={hintId}>{description}</HeaderHint> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

const HINT_WIDTH = 256;
const VIEWPORT_GUTTER = 16;

/**
 * The info glyph and its tooltip.
 *
 * The tooltip is portalled to the body and placed against the viewport, not
 * the card. A card clips its content, and on a narrow card — the profile
 * picture panel, say — a 16rem tooltip anchored inside it was cut off at the
 * card's right edge whichever way it ran. Clamped to the viewport instead, it
 * starts at the glyph and slides left only when the screen edge is near.
 */
function HeaderHint({ id, children }: { id: string; children: React.ReactNode }) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(HINT_WIDTH, window.innerWidth - VIEWPORT_GUTTER * 2);
      const left = Math.min(
        Math.max(rect.left - 8, VIEWPORT_GUTTER),
        window.innerWidth - width - VIEWPORT_GUTTER,
      );
      setPosition({ top: rect.bottom + 8, left });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  const show = () => setOpen(true);
  const hide = () => setOpen(false);

  return (
    <span
      className="relative inline-flex shrink-0"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <span
        ref={triggerRef}
        tabIndex={0}
        aria-label="About this panel"
        className="text-content-subtle hover:text-brand focus-visible:text-brand inline-flex rounded-full transition-colors"
      >
        <InfoIcon className="size-3.5" />
      </span>
      {/* Always in the DOM so `aria-describedby` on the heading resolves. */}
      {createPortal(
        <span
          id={id}
          role="tooltip"
          style={
            position
              ? { top: position.top, left: position.left, width: HINT_WIDTH }
              : { width: HINT_WIDTH }
          }
          className={cn(
            "bg-content text-canvas pointer-events-none fixed z-50 max-w-[calc(100vw-2rem)]",
            "rounded-control px-3 py-2 text-xs leading-relaxed font-normal shadow-lg",
            "transition-opacity duration-150",
            open && position ? "visible opacity-100" : "invisible opacity-0",
          )}
        >
          {children}
        </span>,
        document.body,
      )}
    </span>
  );
}

export function CardBody({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("p-5", className)} {...props}>
      {children}
    </div>
  );
}
